import { useMutation } from '@apollo/client';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { useContext } from 'react';
import { useHistory } from 'react-router';

import { setAuthToken, setOtpVerified } from '@actions/sessionActions';
import { sessionContext } from '@context/Contexts';
import {
  AppleAuthenticationProcessDocument,
  AppleAuthenticationProcessMutation,
  AppleAuthenticationProcessMutationVariables,
  ClientAccessTypeEnum,
  GetMeQuery,
  GoogleAuthenticationProcessDocument,
  GoogleAuthenticationProcessMutation,
  GoogleAuthenticationProcessMutationVariables,
  GoogleClientTypeEnum
} from '@typing/Generated';
import { isAndroidNativeApp, isIosNativeApp, isNativeApp, isWebApp } from '@utils/platformUtils';

type Me =
  | Extract<GetMeQuery['me'], { __typename: 'Admin' }>
  | Extract<GetMeQuery['me'], { __typename: 'Client' }>
  | Extract<GetMeQuery['me'], { __typename: 'Expert' }>;

/* eslint-disable @typescript-eslint/naming-convention */
type AuthResponse = {
  access_token: string;
  access_token_response: {
    access_token: string;
    expires_in: number;
    id_token: string;
    refresh_token: string;
    scope: string;
    token_type: string;
  };
  email: string;
  family_name: string;
  given_name: string;
  hd: string;
  id: string;
  id_token: string;
  locale: string;
  name: string;
  picture: string;
  verified_email: boolean;
};
/* eslint-enable @typescript-eslint/naming-convention */

type Error = {
  errors: string[];
  field: string;
};

type DoAuthParams = {
  includeCalendarScope?: boolean;
  onError?: (errors: Error[] | undefined) => void;
};

const getRedirectUrl = (addPath: boolean = true) => {
  if (isNativeApp()) {
    return 'com.withgrayce.app:/';
  }

  const url = new URL(import.meta.env.VITE_APP_HOST);
  if (addPath) {
    url.pathname = '/auth/google-oauth-callback';
  }
  return url.toString().replace(/\/$/, '');
};

const useHandleNewSession = () => {
  const { dispatch } = useContext(sessionContext);
  const history = useHistory();

  const { sessionState } = useContext(sessionContext);
  const authToken = sessionState.authToken;

  const handleNewSession = (newJwt: string | undefined, user: Me, postAuthUrl?: string | null | undefined) => {
    if (newJwt && newJwt !== authToken) {
      dispatch(setAuthToken(newJwt, user));

      if (user.__typename === 'Client' && user.verificationPhoneNumber?.verified) {
        dispatch(setOtpVerified(false));
      }

      const storedPathname = sessionStorage.getItem('postLoginRedirectTo');
      sessionStorage.removeItem('postLoginRedirectTo');

      if (storedPathname) {
        history.push(storedPathname);
      } else if (postAuthUrl) {
        window.location.href = postAuthUrl;
      } else {
        if (user.__typename === 'Admin') {
          history.push('/admin');
        } else if (user.__typename === 'Client') {
          history.push('/client');
        } else {
          history.push('/expert');
        }
      }
    }
  };
  return handleNewSession;
};

export const useGoogleAuth = () => {
  const [googleAuthenticationProcess, { client }] = useMutation<
    GoogleAuthenticationProcessMutation,
    GoogleAuthenticationProcessMutationVariables
  >(GoogleAuthenticationProcessDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const handleNewSession = useHandleNewSession();

  const doAuth = async ({ includeCalendarScope = false, onError }: DoAuthParams) => {
    const scopeParts = ['email', 'openid', 'profile'];

    const additionalParameters: { [key: string]: string } = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      access_type: 'offline'
    };

    if (includeCalendarScope) {
      scopeParts.push('https://www.googleapis.com/auth/calendar.events');
      additionalParameters.prompt = 'consent';
    }

    localStorage.shouldBlockAppReload = 'yes';

    try {
      await OAuth2Client.authenticate({
        accessTokenEndpoint: 'https://www.googleapis.com/oauth2/v4/token',
        additionalParameters,
        authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/auth',
        logsEnabled: true,
        pkceEnabled: true,
        resourceUrl: 'https://www.googleapis.com/userinfo/v2/me',
        scope: scopeParts.join(' '),
        // eslint-disable-next-line sort-keys
        android: {
          appId: import.meta.env.VITE_GOOGLE_CLIENT_ID_ANDROID,
          pkceEnabled: true,
          redirectUrl: getRedirectUrl(),
          responseType: 'code' // if you configured a android app in google dev console the value must be "code"
        },
        ios: {
          // appId: '843302337501-1e586bnjejo1h6c8o1uf5hfbjo0uq9ak.apps.googleusercontent.com',
          appId: import.meta.env.VITE_GOOGLE_CLIENT_ID_IOS,
          pkceEnabled: true,
          redirectUrl: getRedirectUrl(),
          responseType: 'code' // if you configured a ios app in google dev console the value must be "code"
        },
        web: {
          // clear the tokenEndpoint as we know that implicit flow gets the accessToken from the authorizationRequest
          // accessTokenEndpoint: 'https://oauth2.googleapis.com/token',
          appId: import.meta.env.VITE_GOOGLE_CLIENT_ID_WEB,
          pkceEnabled: false,
          redirectUrl: getRedirectUrl(),
          responseType: 'code', // implicit flow
          windowTarget: '_self'
        }
      }).then((resourceUrlResponse: AuthResponse) => {
        // This promise is only resolved on mobile apps. On the web, the user gets redirected to the redirect route above,
        // which must handle it.
        const clientType = isAndroidNativeApp() ? GoogleClientTypeEnum.ANDROID : GoogleClientTypeEnum.IOS;

        googleAuthenticationProcess({
          variables: {
            // You can't currently use Google to start a paid signup flow
            accessType: ClientAccessTypeEnum.EMPLOYER_SPONSORED,
            clientType,
            idToken: resourceUrlResponse.access_token_response.id_token,
            refreshToken: resourceUrlResponse.access_token_response.refresh_token,
            scope: resourceUrlResponse.access_token_response.scope
          }
        })
          .then(response => {
            if (response.data?.googleAuthenticationProcess?.user) {
              handleNewSession(
                response.data.googleAuthenticationProcess.session?.jwt,
                response.data.googleAuthenticationProcess.user,
                response.data.googleAuthenticationProcess.postAuthUrl
              );
            } else if (
              response.data?.googleAuthenticationProcess?.errors &&
              response.data?.googleAuthenticationProcess?.errors.length > 0
            ) {
              onError?.(response.data.googleAuthenticationProcess.errors);
            }
          })
          .catch(reason => {
            // eslint-disable-next-line no-console
            console.error('Google OAuth rejected', reason);
            onError?.(undefined);
          });
      });
    } catch (reason) {
      // eslint-disable-next-line no-console
      console.error('Google OAuth rejected', reason);
      onError?.(undefined);
    } finally {
      localStorage.shouldBlockAppReload = 'no';
    }
  };

  return doAuth;
};

type SignInWithAppleParams = {
  onError: (errors: Error[] | undefined) => void;
};

export const useSignInWithApple = () => {
  const [appleAuthenticationProcess, { client }] = useMutation<
    AppleAuthenticationProcessMutation,
    AppleAuthenticationProcessMutationVariables
  >(AppleAuthenticationProcessDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const handleNewSession = useHandleNewSession();

  const doAuth = async ({ onError }: SignInWithAppleParams) => {
    localStorage.shouldBlockAppReload = 'yes';
    try {
      if (isIosNativeApp()) {
        await OAuth2Client.authenticate({
          appId: 'com.withgrayce.login',
          authorizationBaseUrl: 'https://appleid.apple.com/auth/authorize',
          ios: {
            redirectUrl: getRedirectUrl(false)
          }
        }).then((resourceUrlResponse: AuthResponse) => {
          appleAuthenticationProcess({
            variables: {
              // You can't currently use Apple to start a paid signup flow
              accessType: ClientAccessTypeEnum.EMPLOYER_SPONSORED,
              firstName: resourceUrlResponse.given_name,
              idToken: resourceUrlResponse.id_token,
              lastName: resourceUrlResponse.family_name
            }
          })
            .then(response => {
              if (response.data?.appleAuthenticationProcess?.user) {
                handleNewSession(
                  response.data.appleAuthenticationProcess.session?.jwt,
                  response.data.appleAuthenticationProcess.user
                );
              } else if (
                response.data?.appleAuthenticationProcess?.errors &&
                response.data?.appleAuthenticationProcess?.errors.length > 0
              ) {
                onError?.(response.data.appleAuthenticationProcess.errors);
              }
            })
            .catch(reason => {
              // eslint-disable-next-line no-console
              console.error('Apple OAuth rejected', reason);
              onError?.(undefined);
            });
        });
      } else if (isWebApp()) {
        (window as any).AppleID.auth.init({
          clientId: 'com.withgrayce.login',
          redirectURI: getRedirectUrl(false),
          scope: 'name email',
          usePopup: true
        });

        // Call Sign In
        try {
          const appleResponse = await (window as any).AppleID.auth.signIn();
          appleAuthenticationProcess({
            variables: {
              // You can't currently use Apple to start a paid signup flow
              accessType: ClientAccessTypeEnum.EMPLOYER_SPONSORED,
              firstName: appleResponse.user?.name?.firstName,
              idToken: appleResponse.authorization.id_token,
              lastName: appleResponse.user?.name?.lastName
            }
          })
            .then(processResponse => {
              if (processResponse.data?.appleAuthenticationProcess?.user) {
                handleNewSession(
                  processResponse.data.appleAuthenticationProcess.session?.jwt,
                  processResponse.data.appleAuthenticationProcess.user
                );
              } else if (
                processResponse.data?.appleAuthenticationProcess?.errors &&
                processResponse.data?.appleAuthenticationProcess?.errors.length > 0
              ) {
                onError?.(processResponse.data.appleAuthenticationProcess.errors);
              }
            })
            .catch(reason => {
              // eslint-disable-next-line no-console
              console.error('Apple OAuth rejected', reason);
              onError?.(undefined);
            });
        } catch {
          onError?.(undefined);
        }
      } else {
        onError(undefined);
      }
    } catch (reason) {
      // eslint-disable-next-line no-console
      console.error('Apple OAuth rejected', reason);
      onError?.(undefined);
    } finally {
      localStorage.shouldBlockAppReload = 'no';
    }
  };

  return doAuth;
};
